import * as React from 'react';
import ScrollToTop from "react-scroll-to-top";
import ChapterNav from './components/ChapterNav';
import MetricCards from './components/MetricCards';
import PullQuote from './components/PullQuote';
import CompactImageCard from './components/CompactImageCard';
import ImageSubsection from './components/ImageSubsection';
import Footer from "../../toolbar/Footer";
import './AppBuilder.css';
import image_AllSections from '../../images/appbuilder/Allsectionsc.jpg';
import image_GridDisplayOptions from '../../images/appbuilder/GridDisplayOptionsc.jpg';
import image_GridDisplayPositioning from '../../images/appbuilder/GridDisplayPositioningc.jpg';
import image_11DashboardConfig from '../../images/appbuilder/1.1dashboardconfigc.jpg';
import image_13OnlyOneLayerVisible from '../../images/appbuilder/1.3only_one_layer_visiblec.jpg';
import image_OldDashboard from '../../images/appbuilder/OldDashboard.png';
import image_OldEditor from '../../images/appbuilder/OldEditor.png';
import image_VisibilityCondition from '../../images/appbuilder/VisibilityCondition.png';
import image_WidgetBrowser from '../../images/appbuilder/WidgetBrowser.png';
import image_TreePanel from '../../images/appbuilder/TreePanel.png';
import image_GridSize from '../../images/appbuilder/GridSize.png';
import image_ParameterOptions from '../../images/appbuilder/ParameterOptions.png';
import image_01MobEditorDisplay from '../../images/appbuilder/0.1MobEditorDisplay.jpg';
import image_03MobEditorWidget from '../../images/appbuilder/0.3MobEditorWidget.jpg';
import image_MobileEditor from '../../images/appbuilder/MobileEditor.png';
import image_SectionsTranslation from '../../images/appbuilder/SectionsTranslation.jpg';
import image_SyncOn from '../../images/appbuilder/SyncOn.png';
import image_SyncOff from '../../images/appbuilder/SyncOff.png';
import video_Google from '../../images/appbuilder/Google.mp4';
import video_GridSize from '../../images/appbuilder/GridSize.mp4';
import image_brainMap from '../../images/appbuilder/brainmap.png';
import image_banner from '../../images/appbuilder/appbuilder_banner.png';
import image_persona1 from '../../images/appbuilder/appbuilder_persona1_2.png';
import image_persona2 from '../../images/appbuilder/appbuilder_persona2_2.png';
import image_persona3 from '../../images/appbuilder/appbuilder_persona3_2.png';
import PageTransition from '../../pageTransition';

const CHAPTERS = [
    { id: 'ch-ab-context', label: 'Problem' },
    { id: 'ch-ab-users', label: 'Users' },
    { id: 'ch-ab-strategy', label: 'Strategy' },
    { id: 'ch-ab-concepts', label: 'Concepts' },
    { id: 'ch-ab-design', label: 'Design' },
    { id: 'ch-ab-mobile', label: 'Mobile' },
    { id: 'ch-ab-impact', label: 'Impact' },
];

const METRICS = [
    { value: 100, suffix: '%', label: 'Department Adoption' },
    { value: 0, suffix: '', label: 'Mockups Required' },
    { value: 2, suffix: '', label: 'Platforms, One URL' },
];

const AppBuilder = (props) => {
    return (
        <>
        <PageTransition>
        <div className="mainBox">
            <div className="appBuilder project odd">
                <div className="project-inner">
                    <ImageSubsection
                        title="App Builder"
                        blue={true}
                        banner={true}
                        images={[
                            { image: image_banner },
                        ]}
                        description='Single-handedly designed the platform&apos;s core tool, adopted company-wide across every department<br/><br/><span style="opacity: 0.6">ROLE:</span> Lead Designer'
                    />

                    <div className="appbuilder-spreads">
                        {/* ========================================= */}
                        {/* SPREAD 1: Context                         */}
                        {/* ========================================= */}
                        <div className="spread-ab-context" id="ch-ab-context">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">01</span>
                                <span className="spread-chapter-label">Problem</span>
                                <p className="spread-chapter-desc">
                                    The Squirro Insights Engine platform needed to evolve from static dashboards to full-fledged applications. The legacy tool lacked navigation and interaction concepts. I proposed a new system that uses dashboards as building blocks while introducing app-like behaviors — sections, layers, visibility conditions, and responsive mobile support.
                                </p>
                            </div>

                            <div className="spread-cell--hero-img" style={{ gridArea: 'oi' }}>
                                <CompactImageCard
                                    image={image_OldEditor}
                                    title="Legacy Dashboard Editor"
                                    caption="The starting point — a dashboard-centric tool missing app cohesiveness"
                                />
                            </div>

                            <div className="spread-cell--video" style={{ gridArea: 'vd' }}>
                                <CompactImageCard
                                    video={video_GridSize}
                                    title="New Dashboard Editor"
                                    caption="The redesign — a section-based visual editor with layers, widgets, and a 48-column grid"
                                />
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 2: Users                           */}
                        {/* ========================================= */}
                        <div className="spread-ab-users" id="ch-ab-users">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">02</span>
                                <span className="spread-chapter-label">Users</span>
                                <p className="spread-chapter-desc">
                                    Three user types interact with the App Builder at different levels of complexity — from layout design to widget configuration to custom development.
                                </p>
                            </div>

                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard image={image_persona3} />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard image={image_persona1} />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard image={image_persona2} />
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 3: Strategy                        */}
                        {/* ========================================= */}
                        <div className="spread-ab-strategy" id="ch-ab-strategy">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">03</span>
                                <span className="spread-chapter-label">Strategy</span>
                                <p className="spread-chapter-desc">
                                    The new tool builds dashboards section by section, composing layers and widgets within a visual editor. With each iteration I moved closer to the vision of building actual apps from dashboards — the mind map below captures the key concepts that made it possible.
                                </p>
                            </div>

                            <div className="spread-cell--hero-img" style={{ gridArea: 'mp' }}>
                                <CompactImageCard image={image_brainMap} title="Design Strategy Mind Map" />
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 4: Concepts                        */}
                        {/* ========================================= */}
                        <div className="spread-ab-concepts" id="ch-ab-concepts">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">04</span>
                                <span className="spread-chapter-label">Concepts</span>
                                <p className="spread-chapter-desc">
                                    Five core concepts power the App Builder — each one a stepping stone from static dashboards to dynamic, interactive applications.
                                </p>
                            </div>

                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard
                                    image={image_AllSections}
                                    title="Dashboard Sections"
                                    caption="Top, Bottom, Left, Right, and Center sections create standardized layout options"
                                />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard
                                    image={image_13OnlyOneLayerVisible}
                                    title="Dashboard Layers"
                                    caption="Containers for widgets — each layer controls its own visibility rules"
                                />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard
                                    image={image_WidgetBrowser}
                                    title="Widget Library"
                                    caption="Tile view with icons under categories like items, navigation, filters, search"
                                />
                            </div>
                            <div className="spread-cell--card4" style={{ gridArea: 'c4' }}>
                                <CompactImageCard
                                    image={image_VisibilityCondition}
                                    title="Visibility Conditions"
                                    caption="Layer property with condition builder using Query, Label, Selection, and Dashboard Store"
                                />
                            </div>
                            <div className="spread-cell--card5" style={{ gridArea: 'c5' }}>
                                <CompactImageCard
                                    image={image_GridDisplayOptions}
                                    title="Resizing Options"
                                    caption="Autosize to fit, preserve minimum, or no auto-sizing — controlling what's above the fold"
                                />
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 5: Design                          */}
                        {/* ========================================= */}
                        <div className="spread-ab-design" id="ch-ab-design">
                            <div className="spread-cell--strategy-text" style={{ gridArea: 'st' }}>
                                <span className="spread-chapter-number">05</span>
                                <span className="spread-chapter-label">Design</span>
                                <h3>From Old to New</h3>
                                <p>
                                    The edit mode UI was inspired by Adobe Creative Suite and Google Data Studio. The dark-themed editor features a tree panel for sections/layers/widgets and a properties panel for configuration. I replaced the 12-column grid with a 48-column system for fine-grained control.
                                </p>
                            </div>

                            <div className="spread-cell--hero-img" style={{ gridArea: 'oi' }}>
                                <CompactImageCard
                                    image={image_OldDashboard}
                                    title="Old Dashboard View"
                                />
                            </div>

                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard
                                    image={image_GridSize}
                                    title="48-Column Grid System"
                                />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard
                                    image={image_TreePanel}
                                    title="Tree Panel & Editor"
                                />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard
                                    image={image_GridDisplayPositioning}
                                    title="Section Positioning"
                                />
                            </div>
                            <div className="spread-cell--card4" style={{ gridArea: 'c4' }}>
                                <CompactImageCard
                                    image={image_11DashboardConfig}
                                    title="Dashboard Configuration"
                                />
                            </div>
                            <div className="spread-cell--card5" style={{ gridArea: 'c5' }}>
                                <CompactImageCard
                                    image={image_ParameterOptions}
                                    title="Parameter Options"
                                />
                            </div>
                            <div className="spread-cell--card6" style={{ gridArea: 'c6' }}>
                                <CompactImageCard
                                    image={image_WidgetBrowser}
                                    title="Widget Library Browser"
                                />
                            </div>

                            <div className="spread-cell--video" style={{ gridArea: 'vd' }}>
                                <video autoPlay muted loop playsInline>
                                    <source src={video_Google} type="video/mp4" />
                                </video>
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 6: Mobile                          */}
                        {/* ========================================= */}
                        <div className="spread-ab-mobile" id="ch-ab-mobile">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">06</span>
                                <span className="spread-chapter-label">Mobile</span>
                                <p className="spread-chapter-desc">
                                    Desktop and mobile modes share the same URL. Widgets span full width on mobile and stack vertically. I designed a sync system between modes and a clever gesture-based translation of desktop sections to mobile — scroll up reveals the header, scroll down the footer, swipe for side panels.
                                </p>
                            </div>

                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard
                                    image={image_SectionsTranslation}
                                    title="Section Translation"
                                    caption="Desktop sections translate to mobile gestures — scroll and swipe interactions"
                                />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard
                                    image={image_01MobEditorDisplay}
                                    title="Mobile Editor Display"
                                />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard
                                    image={image_03MobEditorWidget}
                                    title="Mobile Widget Config"
                                />
                            </div>
                            <div className="spread-cell--card4" style={{ gridArea: 'c4' }}>
                                <CompactImageCard
                                    image={image_MobileEditor}
                                    title="Mobile Editor View"
                                />
                            </div>
                            <div className="spread-cell--card5" style={{ gridArea: 'c5' }}>
                                <CompactImageCard
                                    image={image_SyncOn}
                                    title="Sync On"
                                    caption="Desktop and mobile modes stay synchronized by default"
                                />
                            </div>
                            <div className="spread-cell--card6" style={{ gridArea: 'c6' }}>
                                <CompactImageCard
                                    image={image_SyncOff}
                                    title="Sync Off"
                                    caption="Once edited independently, re-syncing is uni-directional from desktop"
                                />
                            </div>
                        </div>

                        {/* ===== IMPACT METRICS ===== */}
                        <section id="ch-ab-impact">
                            <MetricCards title="Impact" metrics={METRICS} />
                            <PullQuote
                                text="This tool became the backbone of showcasing our capabilities — marketing builds product videos, sales builds pitches, solution engineers build POCs, customers design their own dashboards. It eliminated mockups from the design process entirely."
                                variant="cool"
                            />
                        </section>
                    </div>
                </div>
            </div>
            <Footer/>
        </div>
        </PageTransition>
        <ScrollToTop smooth className="scroll-to-top" />
        <ChapterNav chapters={CHAPTERS} />
        </>
    );
};

export default AppBuilder;
